import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Copy } from 'lucide-react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { apiService } from '@/services/api'
import type { JobRequest } from '@/services/api'
import { useAuth } from '@/shared/hooks/useAuth'
import { userHasPermission } from '@/shared/lib/roleUtils'
import {
  CLAVE_DE_DUPLICADO,
  solicitudDuplicada,
} from '@/features/rh/lib/duplicarSolicitud'

/**
 * Abre el alta de una solicitud ya llenada con los datos de otra (SRH-318).
 *
 * Una plaza que se cerró porque se contrató a alguien, y de la que hace falta
 * otra igual, obligaba a capturar todo de nuevo. Aquí no se crea nada: se abre
 * el formulario de siempre con los datos copiados, y la solicitud nace al
 * enviarlo —con su propio ID y desde el primer paso del flujo—. Si la persona
 * se arrepiente no queda ningún borrador huérfano.
 *
 * Lo ve quien puede levantar solicitudes: la nueva siempre queda a nombre de
 * quien la captura, no de quien pidió la original.
 *
 * Trae la solicitud completa al hacer clic y no usa la fila del listado: el
 * listado no garantiza traer todos los campos del perfil.
 */
export const BotonDuplicarSolicitud = ({
  idSolicitud,
  solicitudCargada,
  className = '',
}: {
  idSolicitud: number | string
  /** El detalle ya la tiene completa; se evita pedirla otra vez. */
  solicitudCargada?: JobRequest | null
  className?: string
}) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [trabajando, setTrabajando] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)

  const puedeCrear = userHasPermission(
    'rh.solicitudes_personal.base.create',
    user?.rbac?.effectivePermissions || [],
  )
  if (!puedeCrear) return null

  const duplicar = async (e: React.MouseEvent) => {
    // En la lista la tarjeta entera es un enlace; sin esto el clic abriría el
    // detalle de la original en vez del formulario.
    e.stopPropagation()
    setFallo(null)
    setTrabajando(true)
    try {
      const original =
        solicitudCargada ?? (await apiService.getJobRequestById(String(idSolicitud)))
      navigate('/job-requests/new', {
        state: { [CLAVE_DE_DUPLICADO]: solicitudDuplicada(original) },
      })
    } catch {
      setFallo('No se pudo copiar la solicitud. Inténtalo de nuevo.')
      setTrabajando(false)
    }
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={duplicar}
        disabled={trabajando}
        title="Crear una solicitud nueva con los mismos datos"
        className={estiloDeBoton('secundario', className)}
      >
        <Copy className="h-4 w-4" />
        {trabajando ? 'Copiando…' : 'Duplicar'}
      </button>
      {fallo && <span className="text-xs text-red-600">{fallo}</span>}
    </span>
  )
}

export default BotonDuplicarSolicitud
